import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, LoaderCircle, X } from 'lucide-react';

export function PageLoader({ label = 'Loading…' }) {
  return (
    <div className="page-loader" role="status">
      <LoaderCircle className="spin" size={28} />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="empty-state">
      {Icon ? <span className="empty-icon"><Icon size={26} /></span> : null}
      <h3>{title}</h3>
      {description ? <p>{description}</p> : null}
      {action}
    </div>
  );
}

export function StatusBadge({ active }) {
  return (
    <span className={`status-badge ${active ? 'active' : 'inactive'}`}>
      <span /> {active ? 'Active' : 'Inactive'}
    </span>
  );
}

export function Modal({ open, title, subtitle, onClose, closeDisabled = false, children, size = 'large' }) {
  useEffect(() => {
    if (!open) return undefined;

    document.documentElement.classList.add('modal-open');
    document.body.classList.add('modal-open');

    const closeOnEscape = (event) => {
      if (event.key === 'Escape' && !closeDisabled) onClose?.();
    };
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.documentElement.classList.remove('modal-open');
      document.body.classList.remove('modal-open');
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [closeDisabled, open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="modal-backdrop" role="presentation">
      <div
        className="modal-viewport"
        onMouseDown={(event) => {
          if (event.currentTarget === event.target && !closeDisabled) onClose?.();
        }}
      >
        <section className={`modal-card ${size}`} role="dialog" aria-modal="true" aria-label={title} aria-busy={closeDisabled}>
          <header className="modal-header">
            <div>
              <h2>{title}</h2>
              {subtitle ? <p>{subtitle}</p> : null}
            </div>
            <button className="icon-button" onClick={onClose} aria-label="Close" disabled={closeDisabled}><X size={20} /></button>
          </header>
          <div className="modal-body">{children}</div>
        </section>
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({ open, title, description, confirmLabel = 'Confirm', danger = false, busy, onConfirm, onClose }) {
  return (
    <Modal open={open} title={title} onClose={busy ? undefined : onClose} closeDisabled={busy} size="small">
      <div className="confirm-content">
        <span className={`confirm-icon ${danger ? 'danger' : ''}`}><AlertTriangle size={25} /></span>
        <p>{description}</p>
      </div>
      <div className="form-actions">
        <button className="button secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button className={`button ${danger ? 'danger' : 'primary'}`} onClick={onConfirm} disabled={busy}>
          {busy ? <LoaderCircle className="spin" size={17} /> : null}{confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

export function Notice({ type = 'info', children, onClose }) {
  return (
    <div className={`notice ${type}`}>
      {type === 'success' ? <CheckCircle2 size={19} /> : <AlertTriangle size={19} />}
      <span>{children}</span>
      {onClose ? <button onClick={onClose} aria-label="Dismiss"><X size={17} /></button> : null}
    </div>
  );
}

export function formatTimestamp(value) {
  if (!value) return '—';
  const date = value?.toDate?.() || new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}
